import { memo, useEffect, useState } from "react";
import { NodeProps } from "react-flow-renderer/dist/nocss";
import { useBoardContext } from "../../contexts/BoardContext";
import { StepDefinition } from "../../stepDefinitions";
import AbstractNode from "../AbstractNode";
import SegmentedControl from "../ui/SegmentedControl";

type HtmlMode = "encode" | "decode";
type HtmlFilterData = { mode: HtmlMode };
type HtmlFilterInputs = { rawValue: string };

const HtmlFilter = memo((props: NodeProps<HtmlFilterData>) => {
  const [mode, setMode] = useState<HtmlMode>(props.data.mode);
  const { updateElementData } = useBoardContext();

  useEffect(() => {
    updateElementData(props.id, { mode });
  }, [updateElementData, props.id, mode]);

  return (
    <AbstractNode
      buttons={
        <SegmentedControl
          options={[
            { label: "Encode", value: "encode" },
            { label: "Decode", value: "decode" },
          ]}
          activeOption={mode}
          setActiveOption={setMode as (value: string) => void}
        />
      }
      {...props}
    />
  );
});

export default HtmlFilter;

function decodeHTMLEntities(text: string) {
  var textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}
function encodeHTMLEntities(text: string) {
  var textArea = document.createElement('textarea');
  textArea.innerText = text;
  return textArea.innerHTML;
}

export const definition: StepDefinition<HtmlFilterData, HtmlFilterInputs> =
  {
    label: "HTML",
    description: "Encodes or decodes the given input to/from sanitized HTML.",
    group: "filter",

    component: HtmlFilter,

    init(data) {
      data.mode =
        data.mode === "encode" || data.mode === "decode" ? data.mode : "encode";
    },

    pipe({ rawValue }, { mode }) {
      try {
        return (
          rawValue &&
          (mode === "decode"
            ? decodeHTMLEntities(rawValue)
            : encodeHTMLEntities(rawValue)
          )
        );
      } catch {}
    },

    inputs: {
      rawValue: {
        label: "Raw Value",
        type: "String",
        required: true,
        acceptMultiple: false,
      },
    },
    outputs: {
      processedValue: {
        label: "Processed Value",
        type: "String",
      },
    },
  };
